
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Spinner } from '@/components/Spinner';
import { toast } from '@/components/ui/use-toast';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { InfoCircledIcon, CheckCircledIcon } from "@radix-ui/react-icons";
import { supabase } from '@/integrations/supabase/client';
import { ensureReferralInURL } from '@/utils/rewardfulUtils';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailVerificationNeeded, setEmailVerificationNeeded] = useState(false);
  const [emailVerificationSuccess, setEmailVerificationSuccess] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const { login, user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const loginAttempted = useRef(false);

  // Simple redirect for authenticated users
  useEffect(() => {
    if (user && !isSubmitting && !authLoading) {
      console.log("User is authenticated, redirecting to home");
      navigate('/', { replace: true });
    }
  }, [user, navigate, isSubmitting, authLoading]);

  // Handle URL parameters for email verification
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailVerificationStatus = params.get('emailVerification');
    const emailParam = params.get('email');
    
    if (emailVerificationStatus === 'required') {
      setEmailVerificationNeeded(true);
      setEmailVerificationSuccess(false);
      if (emailParam) {
        setEmail(emailParam);
      }
    } else if (emailVerificationStatus === 'success') {
      setEmailVerificationNeeded(false);
      setEmailVerificationSuccess(true);
      if (emailParam) {
        setEmail(emailParam);
      }
      setTimeout(() => {
        toast({
          title: "Email verificado",
          description: "Email verificado correctamente. Ahora puedes iniciar sesión.",
        });
      }, 500);
    }
    
    // Clean URL after processing
    if (emailVerificationStatus) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
    
    // Ensure referral ID is maintained
    ensureReferralInURL();
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) {
      console.log("Already submitting, ignoring");
      return;
    }
    
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Por favor, introduce tu email y contraseña",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      setLoginError(null);
      loginAttempted.current = true;
      
      console.log("Attempting login...");
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error('Login error:', error);
        
        if (error.message?.includes('Email not confirmed')) {
          setEmailVerificationNeeded(true);
          setEmailVerificationSuccess(false);
          
          // Resend verification email
          try {
            await supabase.auth.resend({
              type: 'signup',
              email,
            });
            toast({
              title: "Verificación requerida",
              description: "Hemos reenviado el email de verificación. Por favor, revisa tu bandeja de entrada.",
            });
          } catch (resendError) {
            console.error('Error resending verification email:', resendError);
          }
        } else {
          setLoginError(error.message || 'Error al iniciar sesión');
          toast({
            title: "Error de inicio de sesión",
            description: error.message || 'Error al iniciar sesión. Por favor, verifica tus credenciales.',
            variant: "destructive"
          });
        }
        return;
      }
      
      console.log("Login successful, user:", data?.user?.id);
      
      // Clear session flags
      sessionStorage.setItem('redirect_to_subscription_skipped', 'true');
      localStorage.setItem('had_subscription_page_view', 'true');
      
      // Success will be handled by the auth context and redirect effect
      toast({
        title: "¡Bienvenido!",
        description: "Has iniciado sesión correctamente.",
      });
      
    } catch (error: any) {
      console.error('Login error:', error);
      setLoginError(error.message || 'Error al iniciar sesión');
      toast({
        title: "Error",
        description: error.message || 'Error al iniciar sesión. Por favor, inténtalo de nuevo.',
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast({
        title: "Error",
        description: "Por favor, introduce tu email para reenviar la verificación",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      
      if (error) {
        toast({
          title: "Error",
          description: "Error al reenviar el email de verificación. Por favor, inténtalo de nuevo.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Email enviado",
          description: "Hemos reenviado el email de verificación. Por favor, revisa tu bandeja de entrada.",
        });
      }
    } catch (error) {
      console.error('Error resending verification email:', error);
      toast({
        title: "Error",
        description: "Error al reenviar el email de verificación",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading only when actually submitting or auth is loading
  const showLoading = isSubmitting || authLoading;

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      
      <main className="flex-grow flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-vegana-primary">
              Iniciar Sesión
            </CardTitle>
            <CardDescription>
              Introduce tus credenciales para acceder a tu cuenta
            </CardDescription>
          </CardHeader>
          <CardContent>
            {emailVerificationNeeded && (
              <Alert className="mb-6 bg-amber-50 border-amber-200">
                <InfoCircledIcon className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">Verificación de email requerida</AlertTitle>
                <AlertDescription className="text-amber-700">
                  Te hemos enviado un email de confirmación. Por favor, revisa tu bandeja de entrada 
                  (y carpeta de spam) y haz clic en el enlace para activar tu cuenta antes de iniciar sesión.
                  <Button 
                    variant="link" 
                    className="p-0 h-auto text-amber-800 underline mt-2"
                    onClick={handleResendVerification}
                    disabled={isSubmitting}
                  >
                    Reenviar email de verificación
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            
            {emailVerificationSuccess && (
              <Alert className="mb-6 bg-green-50 border-green-200">
                <CheckCircledIcon className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-800">¡Email verificado correctamente!</AlertTitle>
                <AlertDescription className="text-green-700">
                  Tu dirección de correo electrónico ha sido verificada. Ahora puedes iniciar sesión para acceder a tu cuenta.
                </AlertDescription>
              </Alert>
            )}
            
            {loginError && (
              <Alert className="mb-6 bg-red-50 border-red-200">
                <InfoCircledIcon className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-red-800">Error de inicio de sesión</AlertTitle>
                <AlertDescription className="text-red-700">
                  {loginError}
                </AlertDescription>
              </Alert>
            )}
            
            {showLoading ? (
              <div className="py-8 flex justify-center">
                <Spinner size="lg" />
                <p className="ml-3 text-gray-600">
                  {isSubmitting ? 'Iniciando sesión...' : 'Cargando...'}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Contraseña</Label>
                    <Link to="/password-reset" className="text-xs text-vegana-primary hover:text-vegana-accent">
                      ¿Olvidaste tu contraseña?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-vegana-primary hover:bg-vegana-primary/90" 
                  disabled={isSubmitting}
                >
                  Iniciar Sesión
                </Button>
              </form>
            )}
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <p className="text-sm text-gray-600">
              ¿No tienes una cuenta?{' '}
              <Link to="/registro" className="text-vegana-primary hover:underline">
                Regístrate
              </Link>
            </p>
          </CardFooter>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
};

export default Login;
